import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { COUNTRIES, ORGANIZATIONS } from "@/data/organizations";
import { Reveal, Overline } from "@/components/Reveal";

export const Organizations = ({ showLink = false }) => {
  const { lang, t } = useLang();
  const [country, setCountry] = useState("sv");
  const orgs = ORGANIZATIONS.filter((o) => o.active && o.country === country).sort((a, b) => a.order - b.order);
  const pastels = ["bg-pastel-violet", "bg-pastel-blue", "bg-pastel-yellow"];

  return (
    <section className="py-14 md:py-20 border-t border-navy/5" data-testid="organizations-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal className="max-w-3xl">
          <Overline>{t.orgs.overline}</Overline>
          <h2 className="font-display text-4xl md:text-5xl leading-[1.08] tracking-tight text-navy mt-4">
            {t.orgs.title}
          </h2>
          <p className="mt-6 text-base md:text-lg leading-relaxed text-navy/70">{t.orgs.text}</p>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-12 flex flex-wrap gap-2 md:gap-3" role="tablist" aria-label={t.orgs.title}>
            {COUNTRIES.map((c) => {
              const count = ORGANIZATIONS.filter((o) => o.active && o.country === c.code).length;
              const active = country === c.code;
              return (
                <button
                  key={c.code}
                  role="tab"
                  aria-selected={active}
                  onClick={() => setCountry(c.code)}
                  data-testid={`org-tab-${c.code}`}
                  className={`px-5 py-2.5 text-sm font-medium border transition-colors ${
                    active
                      ? "border-ser-purple text-ser-purple bg-pale-violet"
                      : "border-navy/15 text-navy/60 hover:text-navy hover:border-navy/30 bg-white"
                  }`}
                >
                  {c[lang]}
                  <span className={`ml-2 text-xs ${active ? "text-ser-purple/70" : "text-navy/40"}`}>{count}</span>
                </button>
              );
            })}
          </div>
        </Reveal>

        <AnimatePresence mode="wait">
          <motion.div
            key={country}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
            className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4"
          >
            {orgs.map((o, i) => (
              <div
                key={o.name}
                data-testid={`org-tile-${o.name.slice(0, 24).replace(/\W+/g, "-").toLowerCase()}`}
                title={o.name}
                className={`h-32 flex items-center justify-center text-center px-3 py-3 border transition-colors ${o.logo ? "bg-white border-navy/10 hover:border-ser-purple/60" : `${pastels[i % 3]} border-navy/10 hover:border-ser-purple/50`}`}
              >
                {o.logo ? (
                  <img src={o.logo} alt={`${t.orgs.logoAltPrefix} ${o.name}`} className="max-h-full max-w-full w-auto h-auto object-contain" loading="lazy" />
                ) : (
                  <span className="org-tile-name text-sm font-medium leading-snug text-navy/80">{o.name}</span>
                )}
              </div>
            ))}
          </motion.div>
        </AnimatePresence>

        {showLink && (
          <Reveal delay={0.1}>
            <Link
              to={`/${lang}/about`}
              data-testid="organizations-more-link"
              className="group mt-12 inline-flex items-center gap-2 text-sm font-medium text-ser-purple hover:text-navy transition-colors"
            >
              {t.orgs.link}
              <ArrowRight size={16} strokeWidth={1.5} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        )}
      </div>
    </section>
  );
};
